import React, { Fragment, useState, useEffect } from "react";
import { TextFilter } from "./Filters/TextFilter";
import Pagination from "../Pagination";
import cx from "classnames";
import { DefaultTitle } from "./Titles/DefaultTitle";
import ReactTooltip from "react-tooltip";

export const SkeletonDefaults = {
    td: "",
    tr: ""
};

// Create a component which walks through all Column definitions and find if a skeleton rendering element is present
// The resulting component is memoized as the result is always identical
const FakeRow = React.memo((props) => {
    return (
        <tr className={cx(props?.className, SkeletonDefaults?.tr)}>
            {props.columns.map((column, idx) => {
                const Skeleton = column?.Skeleton || ((props) => <div className={"table-skeleton"} />);
                return (
                    <td className={SkeletonDefaults?.td} key={idx}>
                        <Skeleton />
                    </td>
                );
            })}
        </tr>
    );
});

// This is the 'view' part of the Overview component, it is only like templating and shouldn't do much logic
export default function OverviewDisplay(props) {
    const classNamePrefix = props?.classNamePrefix ? props.classNamePrefix : "overview-redux";
    const depricateOldStyling = props?.depricateOldStyling ? props.depricateOldStyling : false;
    const displaySummary = props?.displaySummary ? props.displaySummary : false;

    const [filtersOpen, setFilterOpen] = useState(props.filterInitialOpen);
    const [bulkAction, setBulkAction] = useState(false);

    const RowStructure = props.RowStructure;

    const noFilter = !props.columns.some((col) => col.Filter !== undefined);

    const checkBulkValue = (bulk, row) => (bulk && typeof bulk === "function" ? bulk(row) : false);

    const getAllCurrentItems = () => {
        // we loop the data set which contains multiple pages
        return (
            props.overview?.data?.results
                ?.map((item) => {
                    // for every result we find the bulkvalue as is done when individually checking them
                    return checkBulkValue(props.bulkValue, item) || item;
                })
                ?.flat() || []
        );
    };

    const nonCancelledStudents = getAllCurrentItems()?.filter((o) => !props.bulkData[o]?.cancelled);
    const allNonCancelledChecked = nonCancelledStudents?.every((o) => props.bulkState?.[o] === true) || false;

    // preselect the first item when a bulkoption dropdown is used
    useEffect(() => {
        if (props?.bulkOptions?.length > 0 && bulkAction === false) {
            setBulkAction(props?.bulkOptions[0]);
        }

        if (props?.bulkOptions?.length <= 0 || !props?.bulkOptions) {
            setBulkAction(false);
        }
    }, [bulkAction, props?.bulkOptions]);

    // When bulk-select is enabled and we initially loaded, pre-select all
    useEffect(() => {
        if (props.bulkEnabled === true && props.count > 0 && props.bulkPreselectAll === true) {
            if (Object.entries(props?.bulkState).length === 0) {
                props.setMultiBulk(getAllCurrentItems(), true);
            }
        }
    }, [props?.bulkEnabled, props?.bulkPreselectAll, props?.count, props?.bulkState]);

    useEffect(() => {
        if (props.bulkEnabled === true) {
            const bulkData = props.overview?.data?.results?.map((row) => {
                return {
                    [checkBulkValue(props.bulkValue, row)]: row
                };
            });
            if (bulkData?.length > 0) {
                props.setBulkData(Object.assign({}, ...bulkData));
            }
            Object.keys(props.bulkData).forEach((key) => {
                if (props.bulkData[key]?.cancelled) {
                    props.unSelectInBulk(key);
                }
            });
        }
    }, [props?.bulkEnabled, props.overview?.data?.results]);

    const hasSelectedCancelledStudent = Object.keys(props.bulkData).some((key) => props.bulkData[key].cancelled && props.bulkState[key] === true);
    return (
        <>
            <div className={"overview-top-bar"}>
                {(props?.title || (props.bulkEnabled && props?.bulkOptions?.length > 0)) && (
                    <div>
                        {props.title && <>{typeof props.title === "function" ? props.title(props) : props?.title}</>}
                        {props.bulkEnabled && props?.bulkOptions?.length > 0 /*&& Object.values(props.bulkState).some(o => o === true) */ && (
                            <div className={"bulk-actions"}>
                                <svg className="bulk-actions__arrow" width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 19V18H7V19H8ZM37 19L27 13.2265V24.7735L37 19ZM9 38V19H7V38H9ZM8 20H28V18H8V20Z" fill="#ff6600" />
                                </svg>
                                {props.bulkOptions.map((bulkAction) => {

                                    const isGradesheetDisabled = () =>
                                        !props.contextData?.courseDetail?.events?.some((event) => event?.type === "knowledge-exam") &&
                                        bulkAction?.bulkIdentifier === "gradesheet-download-students";

                                    const hasActiveBulkState = () => Object.values(props.bulkState).some((state) => state === true);

                                    // Calculate disabled state
                                    const disabled =
                                        !hasActiveBulkState() ||
                                        (hasSelectedCancelledStudent && bulkAction?.bulkIdentifier !== "copy-students") ||
                                        isGradesheetDisabled();

                                    return (
                                        <span key={bulkAction.bulkIdentifier} data-tip data-for={`overview-bulkselect-${bulkAction?.bulkIdentifier}`}>
                                            <button
                                                className={cx("btn btn-primary no-sideflap", {
                                                    "bulk-button--disabled": disabled
                                                })}
                                                disabled={disabled}
                                                type="button"
                                                onClick={() => {
                                                    if (bulkAction && bulkAction?.callback && typeof bulkAction.callback === "function") {
                                                        bulkAction.callback({
                                                            items: getAllCurrentItems().filter((o) => props.bulkState?.[o] === true),
                                                            contextData: props.contextData,
                                                            onSuccess: () => {
                                                                props.setMultiBulk(getAllCurrentItems(), false);
                                                            },
                                                            refresh: props.refresh,
                                                            bulkData: props?.bulkData
                                                        });
                                                    }
                                                }}
                                            >
                                                <i className={cx("fa", bulkAction?.icon)} />
                                                {bulkAction?.name && (
                                                    <ReactTooltip
                                                        id={`overview-bulkselect-${bulkAction?.bulkIdentifier}`}
                                                        effect="solid"
                                                        place="top"
                                                        delayShow={250}
                                                        delayHide={125}
                                                    >
                                                        {bulkAction?.name}
                                                    </ReactTooltip>
                                                )}
                                                {hasSelectedCancelledStudent && (bulkAction.bulkIdentifier === "copy-students") && (
                                                    <ReactTooltip
                                                        id={`overview-bulkselect-${bulkAction?.bulkIdentifier}`}
                                                        effect="solid"
                                                        place="top"
                                                        delayShow={250}
                                                        delayHide={125}
                                                    >
                                                        Een of meerdere geselecteerde cursisten zijn geannuleerd in de cursus. Deselecteer eerst de geannuleerde cursisten
                                                        voor dat deze actie kan worden uitgevoerd.
                                                    </ReactTooltip>
                                                )}
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
                {props.topbar && (
                    <div className={`${classNamePrefix}-filters`}>
                        <div className={`${classNamePrefix}-filters__controls}`}>
                            <button className={`${classNamePrefix}-filters__toggle`} onClick={() => setFilterOpen(!filtersOpen)}>
                                <i className={cx("fa", filtersOpen ? "fa-angle-down" : "fa-angle-right")} /> Filters
                            </button>
                            <button className={`${classNamePrefix}-filters__reset`} onClick={() => props.resetFilters()}>
                                Verwijder filters
                            </button>
                        </div>
                        <div className={cx(`${classNamePrefix}-filters__inputs`, { [`${classNamePrefix}-filters__inputs--open`]: filtersOpen })}>
                            {props.topbar.map((item, idx) => {
                                const Component = item.Component;
                                return (
                                    <div key={item.title + "-" + idx} className={`${classNamePrefix}-filters__input`}>
                                        {/*<h2>{item.title}</h2>*/}
                                        <Component
                                            columnIdentifier={item?.columnIdentifier}
                                            setFilter={props.setFilter}
                                            orderingState={props.orderingState}
                                            filterState={props.filterState}
                                            contextData={props.contextData}
                                            // resetFilters={props.resetFilters}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {props.quickview?.display !== false && props.quickview?.quickviews?.length > 1 && (
                    <ul className="quick-views">
                        {props.quickview &&
                            props.quickview.quickviews.map((quickviewItem, idx) => {
                                const prefixContent = quickviewItem?.prefixContent || "";

                                return (
                                    <li key={quickviewItem.identifier} className={"quick-views__item"}>
                                        <button
                                            className={cx("quick-views__link", { "quick-views__link--active": quickviewItem.identifier === props.quickview.quickview })}
                                            onClick={() => props.quickview.setQuickview(quickviewItem.identifier)}
                                        >
                                            {/*<div className="quickview-prefix">*/}
                                            {prefixContent}&nbsp;
                                            {/*</div>*/}
                                            {quickviewItem.name}
                                        </button>
                                    </li>
                                );
                            })}
                    </ul>
                )}
                {(props?.overview?.data?.count === 0 || props?.overview?.data?.count > 0) && !props?.displaySummary && (
                    <div className={`${classNamePrefix}-result-count`}>
                        {props.overview?.data?.count} {props?.overview?.data?.count === 1 ? "resultaat" : "resultaten"}
                    </div>
                )}
                {props?.actions?.length > 0 && (
                    <div className="overview-top-bar__buttons">
                        {props?.actions
                            ?.filter((action) => action?.identifier)
                            ?.map((action) => {
                                if (action?.Component && typeof action?.Component === "function") {
                                    const Component = action.Component;
                                    return <Component key={action.identifier} {...props} />;
                                }
                                return (
                                    <React.Fragment key={action.identifier}>
                                        <button className="btn btn-primary no-sideflap">
                                            {action.title}
                                            {action?.icon && <i className={action?.icon} />}
                                        </button>
                                    </React.Fragment>
                                );
                            })}
                    </div>
                )}
            </div>
            {props?.search && props.search?.columnIdentifier && (
                <div className={`${classNamePrefix}-search`}>
                    <i className={cx("fa fa-search", `${classNamePrefix}-search__icon`)} />
                    <TextFilter
                        columnIdentifier={props.search.columnIdentifier}
                        placeholder={props.search?.placeholder || ""}
                        setFilter={props.setFilter}
                        state={props.filterState?.[props.search.columnIdentifier]}
                        className={`${classNamePrefix}-search__input`}
                    />
                </div>
            )}
            <div className={`${classNamePrefix}-container`}>
                <table
                    className={cx(
                        {
                            table: !depricateOldStyling,
                            "table-clickable": !depricateOldStyling,
                            "table-hover": !depricateOldStyling
                        },
                        classNamePrefix
                    )}
                >
                    <thead>
                        <tr>
                            {props?.bulkEnabled && (
                                <th className={cx(`${classNamePrefix}__head`, `${classNamePrefix}__head--bulk-checkbox`)}>
                                    <input
                                        type={"checkbox"}
                                        title={"Selecteer alle huidige items"}
                                        className={"checkbox checkbox--no-label"}
                                        disabled={nonCancelledStudents.length === 0}
                                        onChange={(e) => {
                                            props.setMultiBulk(nonCancelledStudents, !!e?.target?.checked);
                                        }}
                                        checked={allNonCancelledChecked}
                                        id={"overview-checkbox-check-all"}
                                    />
                                </th>
                            )}
                            {props.columns
                                .filter((column) => column?.noDisplay !== true)
                                .map((column, idx) => {
                                    let Filter = noFilter ? () => "" : () => <input disabled={true} className={"text-filter-spacer"} />; // provide an empty default (could be used for a placeholder element)

                                    // Default to a title
                                    let Title = DefaultTitle;

                                    // define what we want to pass to the Filter component
                                    let additionalProps = {
                                        setFilter: props.setFilter,
                                        columnIdentifier: column.columnIdentifier,
                                        placeholder: column?.placeholder || "",
                                        contextData: props?.contextData || {},
                                        title: column?.title || ""
                                    };

                                    if (column?.Title) {
                                        Title = column.Title;
                                        additionalProps = Object.assign(additionalProps, column?.TitleOptions);
                                    }

                                    if (column?.Filter) {
                                        // Override the default Filter with the passed filter and
                                        Filter = column.Filter;

                                        // Pass allong the additional settings for the filter
                                        additionalProps = Object.assign(
                                            additionalProps,
                                            { filterOptions: column?.FilterOptions || [] },
                                            { state: props?.filterState?.[column.columnIdentifier] !== undefined ? props?.filterState?.[column.columnIdentifier] : "" }
                                        );
                                    }

                                    return (
                                        <th
                                            key={idx}
                                            className={cx(
                                                `${classNamePrefix}__head`,
                                                `${classNamePrefix}__head--${column.columnIdentifier}`,
                                                column?.className,
                                                typeof column?.classNameFn === "function" ? column.classNameFn(additionalProps) : ""
                                            )}
                                        >
                                            <div
                                                className={cx("table-head-title", {
                                                    "table-head-title--orderable": column.orderable,
                                                    "table-head-title--active": column.columnIdentifier === props.orderingState.columnIdentifier,
                                                    "table-head-title--descending":
                                                        column.columnIdentifier === props.orderingState.columnIdentifier && props.orderingState.direction === "-"
                                                })}
                                                onClick={() => column?.orderable === true && props.setOrder(column.columnIdentifier)}
                                            >
                                                <Title {...additionalProps} />
                                                {column.orderable && column.columnIdentifier === props.orderingState.columnIdentifier && (
                                                    <i className={cx("fa", props.orderingState.direction === "-" ? "fa-angle-down" : "fa-angle-up")} />
                                                )}
                                            </div>
                                            <div className={additionalProps?.filterOptions?.className}>
                                                <Filter {...additionalProps} />
                                            </div>
                                        </th>
                                    );
                                })}
                        </tr>
                    </thead>

                    <tbody>
                        {props?.displayFake !== true && !(props?.overview?.data?.results?.length > 0) && (
                            <tr>
                                <td colSpan={props.columns.length}>Geen resultaten gevonden</td>
                            </tr>
                        )}
                        {props.overview?.data?.results?.map((row, idx) => {
                            const bulkValueResult = checkBulkValue(props.bulkValue, row);
                            const checked = props.bulkState?.[bulkValueResult] || false;
                            const check = (e) => props.setBulk(bulkValueResult || row);
                            const BulkColumn = (p) => {
                                return props?.bulkEnabled ? (
                                    <td
                                        className={cx("overview-table__cell", {
                                            "overview-table__cell--row-selected": checked
                                        })}
                                        onClick={check}
                                    >
                                        <input
                                            onChange={check}
                                            disabled={row.cancelled}
                                            type={"checkbox"}
                                            className={"checkbox checkbox--no-label"}
                                            checked={row.cancelled ? false : checked}
                                            id={"overview-checkbox-" + (row?.uuid || row?.id || row?.pk || idx)}
                                        />
                                        {/*<label htmlFor={"overview-checkbox-" + (row?.uuid || row?.id || row?.pk || idx)}>Selecteer rij</label>*/}
                                    </td>
                                ) : null;
                            };

                            // If data indicates that the row is fake, return a fake/skeleton row
                            if (row.fake) {
                                return (
                                    <Fragment key={idx}>
                                        <FakeRow columns={props.columns} className={"skeleton-row"} />
                                    </Fragment>
                                );
                            }

                            return (
                                <RowStructure
                                    unSelectInBulk={props.unSelectInBulk}
                                    bulkColumn={<BulkColumn />}
                                    onClick={props?.onClick || false}
                                    key={idx}
                                    columns={props.columns}
                                    contextData={props?.contextData}
                                    refresh={props?.refresh}
                                    {...row}
                                />
                            );
                        })}
                        {props?.displayFake === true &&
                            props?.additions.map((obj, idx) => {
                                return (
                                    <Fragment key={idx}>
                                        <FakeRow columns={props.columns} className={"filler-row"} />
                                    </Fragment>
                                );
                            })}
                    </tbody>
                </table>
            </div>
            {props.loadMoreEnabled ? (
                <div className="load-more-container">
                    {props.hasMoreData && (
                        <button className="btn btn-primary load-more-button" onClick={props.handleLoadMore} disabled={props.overview.isLoading}>
                            {props.overview.isLoading ? (
                                <span>
                                    <i className="fa fa-spinner fa-spin" /> Laden...
                                </span>
                            ) : (
                                <span>Meer laden</span>
                            )}
                        </button>
                    )}
                </div>
            ) : (
                (props?.overview?.data?.count > 0 || props?.displayFake) && (
                    <Pagination
                        displayFake={props?.displayFake || false}
                        displaySummary={props?.displaySummary || false}
                        count={props.overview.data.count}
                        limit={props.overview.data.limit}
                        offset={props.overview.data.offset}
                        pageSwitch={(offset) => props.setOffset(offset)}
                    />
                )
            )}
        </>
    );
}
